import { yupResolver } from '@hookform/resolvers/yup';
import {
  CLAIM_STATUSES,
  useClaimUpdateNeedAction,
  useDocumentsDelete,
  useViewClaimDetails,
} from 'all-in-one-shared-api';
import { Space } from 'antd';
import { useUploadFiles } from 'api/fileManager';
import NewClaimIcon from 'assets/images/home/new-claim.svg';
import { GenericResult, PageTitle, PrimaryButton, SecondaryButton, Spinner } from 'components';
import { useClaimPropertiesContext } from 'contexts/claimProperties';
import { useCachedClaimProperties, useKeycloakReady } from 'hooks';
import { MessageType } from 'models/MessageType';
import DocumentsCard, {
  getNeededRemoveDocuments,
  getUploadDocumentParams,
} from '../../../newClaim/components/DocumentsCard';
import { setEditClaimFormInput } from '../../../newClaim/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDateString, getClaimPageUrl } from 'utils';
import { isLocaleEnglish } from 'utils/languageUtils';
import { ClaimFormState, getClaimSchema } from './schema';
import { getRequiredDocsBasedOnClaimType } from '../../../newClaim/hooks/useSelectedClaimTypeMapping';
import { excludeRemovedDocuments } from '../../../newClaim/schema';
import { FileUploaded } from '../../../../components/form';
import { SubmitProcess } from '../EditClaimForm';

interface AddClaimDocumentsProps {
  claimId: string;
  sequenceNo?: number;
  isShortfall?: boolean;
}

// TODO(Anson Kan): UI here is done but here has probably state issues because documents are not uploading at all. Will follow up in a dedicated PR.
export const AddClaimDocuments = ({ claimId, sequenceNo, isShortfall }: AddClaimDocumentsProps): JSX.Element => {
  const { ready, token } = useKeycloakReady();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [inited, setInited] = useState(false);

  const [claimNo, setClaimNo] = useState('');
  const changesDocumentProcess = useRef<SubmitProcess[]>([]);
  const deletedFileNames = useRef<string>('');
  const methods = useForm<ClaimFormState>({
    resolver: yupResolver(getClaimSchema(t)),
  });

  const { data, shouldFetch, isLoading, error } = useViewClaimDetails(
    { name: 'useViewClaimDetails', params: { claimId, sequenceNo, isShortfall } },
    token || '',
    claimId,
    sequenceNo,
    {},
    isShortfall,
  );

  const claimNeedActionPageUrl = useMemo(
    () => getClaimPageUrl({ claimId, sequenceNo, claimStatus: CLAIM_STATUSES.NEED_ACTION }),
    [claimId, sequenceNo],
  );
  const claimSubmittedPageUrl = useMemo(
    () => getClaimPageUrl({ referenceNo: claimNo, claimStatus: CLAIM_STATUSES.SUBMITTED }),
    [claimNo],
  );

  const { shouldFetch: updateNeedActionShouldFetch } = useClaimUpdateNeedAction(
    { name: 'useClaimUpdateNeedAction', params: { claimId, sequenceNo } },
    token || '',
    claimId,
    sequenceNo,
    {
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      revalidateOnFocus: false,
      onSuccess: (data) => {
        updateNeedActionShouldFetch(false);
        handleDocumentUpload(data.referenceNo);
      },
      onError: () =>
        claimNeedActionPageUrl &&
        navigate(claimNeedActionPageUrl, {
          state: {
            messageType: MessageType.ERROR,
            messageContent: t('editClaim.needActionUpdateError'),
          },
          replace: true,
        }),
    },
  );

  const { key: claimPropertiesKey } = useClaimPropertiesContext();
  const { data: claimProperties } = useCachedClaimProperties(claimPropertiesKey);

  const selectedClaimType = data?.claimType?.code || '';
  const benefitsCode = data?.benefits.map((benefit) => benefit.code || '') || [];
  const dataForDocuments = getRequiredDocsBasedOnClaimType(
    selectedClaimType,
    benefitsCode,
    claimProperties,
    i18n.language,
  );

  useEffect(() => {
    shouldFetch(ready);

    if (!inited && ready) setInited(true);
  }, [inited, ready, shouldFetch]);

  useEffect(() => {
    if (!data) return;
    methods.reset(setEditClaimFormInput(claimProperties?.claimTypes || [], isLocaleEnglish(i18n.language), data));
  }, [data, i18n.language, methods, claimProperties?.claimTypes]);

  const getUploadFilesParams = () => {
    return {
      formData: methods.getValues(),
      claimId: claimNo,
      treatmentDate: data ? formatDateString(data.treatmentDate) : '',
      ...getUploadDocumentParams(methods.getValues()),
    };
  };

  const { shouldFetch: uploadDocumentsShouldFetch } = useUploadFiles(
    { name: 'useUploadFiles', params: getUploadFilesParams() },
    token || '',
    getUploadFilesParams(),
    {
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      revalidateOnFocus: false,
      onSuccess: () => {
        uploadDocumentsShouldFetch(false);
        navigateAfterSubmit(SubmitProcess.UPLOAD_FILES, true);
      },
      onError: () => navigateAfterSubmit(SubmitProcess.UPLOAD_FILES),
    },
  );

  const navigateAfterSubmit = (action: SubmitProcess, isSucceed = false) => {
    if (changesDocumentProcess === undefined) return;
    const excludeProcessFinished = changesDocumentProcess.current.filter((process) => process !== action);
    changesDocumentProcess.current = excludeProcessFinished;

    if (excludeProcessFinished.length !== 0 && isSucceed) return;

    let state = {
      messageType: MessageType.ERROR,
      messageContent: t('editClaim.addDocumentsError'),
    };
    if (isSucceed) {
      state = {
        messageType: MessageType.SUCCESS,
        messageContent: t('editClaim.addDocumentsSuccess'),
      };
    }

    setTimeout(() => {
      claimSubmittedPageUrl &&
        navigate(claimSubmittedPageUrl, {
          state: state,
          replace: true,
        });
    }, 1000);
  };

  const { shouldFetch: deleteDocumentsShouldFetch } = useDocumentsDelete(
    { name: 'deleteFiles' },
    token || '',
    claimNo || '',
    deletedFileNames.current,
    {
      onSuccess: () => {
        deleteDocumentsShouldFetch(false);
        uploadDocumentsShouldFetch(true);
        navigateAfterSubmit(SubmitProcess.DELETE_FILES, true);
      },
      onError: () => {
        navigateAfterSubmit(SubmitProcess.DELETE_FILES);
      },
    },
  );

  const checkDeleteAndUploadFiles = useCallback(() => {
    const values = methods.getValues();
    const deletedFileName = getNeededRemoveDocuments(values);
    if (deletedFileName) {
      changesDocumentProcess.current = [
        ...changesDocumentProcess.current,
        SubmitProcess.DELETE_FILES,
        SubmitProcess.UPLOAD_FILES,
      ];
      deletedFileNames.current = deletedFileName;
      deleteDocumentsShouldFetch(true);
    } else {
      const excludedRemovedDocuments = excludeRemovedDocuments([
        ...(values.receipts || []),
        ...(values.referralLetters || []),
        ...(values.otherDocuments || []),
        ...(values.prevClaimSettlements || []),
      ]);
      const neededUploadFiles = excludedRemovedDocuments.filter((file: FileUploaded) => file.type);
      const hasNewDocuments = neededUploadFiles.length > 0;
      if (hasNewDocuments) {
        changesDocumentProcess.current = [...changesDocumentProcess.current, SubmitProcess.UPLOAD_FILES];
        uploadDocumentsShouldFetch(true);
      }
    }
  }, [deleteDocumentsShouldFetch, methods, uploadDocumentsShouldFetch]);

  const handleDocumentUpload = useCallback(
    (referenceNo: string) => {
      setClaimNo(referenceNo);
      checkDeleteAndUploadFiles();
    },
    [checkDeleteAndUploadFiles],
  );

  const onSubmit = () => {
    setClaimNo(claimId);
    updateNeedActionShouldFetch(true);
  };

  if (!inited || isLoading) {
    // Note(Anson Kan): to prevent showing the error before start fetching
    return <Spinner />;
  }

  if (error) {
    return <GenericResult status="error" defaultTo="/claims" />;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Space direction="vertical" size={16}>
          <PageTitle
            icon={NewClaimIcon}
            title={`${t('editClaim.addDocuments.title')} #${claimId}`}
            alt="add documents"
          />

          <DocumentsCard formStepIndex={1} selectedClaimTypeObject={dataForDocuments} allowRemoveDocuments={true} />
          <Space>
            <PrimaryButton loading={isLoading} htmlType="submit">
              {t('editClaim.update')}
            </PrimaryButton>

            <SecondaryButton onClick={() => navigate(-1)}>{t('common.cancel')}</SecondaryButton>
          </Space>
        </Space>
      </form>
    </FormProvider>
  );
};
